import { type ReactNode } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import { Button, Stack, type StackProps } from '@mui/material';
import {
  ImperativeFormDialogRefType,
  useGetImperativeOpen,
} from '@shared/components/UserFormDialog';
import { useIntlTranslations } from '@libs/intl/intl.hooks';

type ImperativeDialogProps = {
  ref: React.Ref<ImperativeFormDialogRefType>;
  title: string;
  dialogHelperText?: ReactNode;
  actions?: ReactNode;
  hideDefaultActions?: boolean;
  onConfirm?: () => void;
  content?: ReactNode;
  contentStackProps?: StackProps;
  children?: ReactNode;
};

export const ImperativeDialog = ({
  ref,
  title,
  dialogHelperText,
  actions,
  hideDefaultActions,
  onConfirm,
  content,
  contentStackProps,
  children,
}: ImperativeDialogProps) => {
  const { t } = useIntlTranslations();
  const { open, close } = useGetImperativeOpen<object>(ref);

  const handleConfirm = () => {
    onConfirm?.();
    close();
  };

  return (
    <Dialog
      open={open}
      onClose={close}
      slotProps={{ paper: { sx: { width: { md: '40vw', xs: '100vw' } } } }}
    >
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        {dialogHelperText &&
          (typeof dialogHelperText === 'string' ? (
            <DialogContentText>{dialogHelperText}</DialogContentText>
          ) : (
            dialogHelperText
          ))}
        <Stack direction="column" rowGap={2} paddingY={2} {...contentStackProps}>
          {content ?? children}
        </Stack>
      </DialogContent>
      <DialogActions>
        {actions}
        {!hideDefaultActions && (
          <Stack direction="row" columnGap={2}>
            <Button onClick={close}>{t('formDialog.close')}</Button>
            {onConfirm && <Button onClick={handleConfirm}>{t('formDialog.save')}</Button>}
          </Stack>
        )}
      </DialogActions>
    </Dialog>
  );
};
